import { INTEGRATION_TYPE_DISPLAY, type IntegrationTypeId } from '@steerfi/shared';
import { IconAlertTriangle } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import {
  deleteIntegrationProfile,
  type IntegrationProfileApiRow,
} from '@/api/integration-profiles';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteIntegrationProfileDialogProps {
  profile: IntegrationProfileApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const DeleteIntegrationProfileDialog: React.FC<DeleteIntegrationProfileDialogProps> = ({
  profile,
  onOpenChange,
  onSuccess,
}) => {
  const open = profile !== null;
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) setErrorMsg(null);
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setErrorMsg(null);
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!profile) return;
    try {
      setSubmitting(true);
      setErrorMsg(null);
      await deleteIntegrationProfile(profile.partyId, profile.id);
      handleOpenChange(false);
      onSuccess();
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to delete integration profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (!profile) return null;

  const typeLabel =
    INTEGRATION_TYPE_DISPLAY[profile.integrationTypeId as IntegrationTypeId] ??
    String(profile.integrationTypeId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <IconAlertTriangle size={20} className="text-amber-700 dark:text-amber-400" />
            </div>
            <div className="space-y-1 pt-0.5">
              <DialogTitle>Delete integration profile?</DialogTitle>
              <DialogDescription>
                This will permanently remove <strong>{profile.profileName}</strong> ({typeLabel} ·{' '}
                {profile.environment}). Any payment runs that depend on this profile will fail until
                reconfigured.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
